<template>
  <div class="row">
    <div class="col">
      <PaginatedTable id="paginated-administrativeGroupsTable"
                      :fields="fields"
                      :on-page-change="getGroups"
                      :values-to-save="{searchValue: props.filterText, status: props.filterStatus}"
                      ref="groupsTableRef"
      >
        <template v-slot:cell(action)="{ item }">
          <div class="d-flex align-items-center gap-2">
            <router-link :to="{ name: 'ManageGroup', params: { id: `${item.id}` } }">
              <Button class="me-3" :label="t('CHANGE')"/>
            </router-link>

            <Button
                v-if="isAdmin()"
                icon="pi pi-times"
                text
                rounded
                severity="danger"
                v-tooltip="t('REMOVE')"
                @click="deleteGroup(item)"
            />
          </div>
        </template>
        <template v-slot:cell(location)="{ item }">
          <span v-if="item.location === GroupLocation.IN_HOUSE">{{ t("IN_HOUSE") }}</span>
          <span v-else-if="item.location === GroupLocation.ONLINE">{{ t("ONLINE") }}</span>
          <span v-else>{{ item.location }}</span>
        </template>
        <template v-slot:cell(status)="{ item }">
          <span>{{ t(item.status) }}</span>
        </template>
        <template v-slot:cell(title)="{ item }">
          <span>{{ item.title }}</span>
        </template>
        <template v-slot:cell(language)="{ item }">
          <span>{{ getGroupLangOptions(t).find(v => v.value === item.language)?.text ?? item.language }}</span>
        </template>
        <template v-slot:cell(startEvent)="{ item }">
          <span v-if="item.startEvent">{{ dateFormat(item.startEvent) }}</span>
        </template>
        <template v-slot:cell(endEvent)="{ item }">
          <span v-if="item.endEvent">{{ dateFormat(item.endEvent) }}</span>
        </template>
        <template v-slot:cell(businessName)="{ item }">
          <span>{{ item.businessName }}</span>
        </template>

        <template v-slot:cell(trainers)="{ item }">
          <ul class="mb-0 ps-0" style="list-style-type: disc;" v-if="item.trainers?.length">
            <li v-for="i in item.trainers" :key="i.trainers">
              {{ i }}
            </li>
          </ul>
        </template>
        <template v-slot:cell(groupSize)="{ item }">
          <span>{{ item.numberOfTrainees || 0 }} / {{ item.groupSize }}</span>
        </template>
      </PaginatedTable>
    </div>
  </div>
  <RemoveGroupModal @group-removed="resetGroupsTable"/>
</template>

<script setup>

import EventBus from "../../../util/eventBus";
import {useDateFormat} from "@/mixins/dateFormatMixin2.ts";
import {computed, ref, watch} from "vue";
import {useI18n} from "vue-i18n";
import {injectAuth, injectAxios} from "@/util/inject";

import RemoveGroupModal from "@/views/components/modals/removeGroupModal.vue";
import {GroupLocation} from "@/model/group-location";
import PaginatedTable from "@/views/components/PaginatedTable.vue";
import {getGroupLangOptions} from "@/model/group";

const auth = injectAuth();
const {dateFormat} = useDateFormat();

const props = defineProps({
  filterText: {
    type: String,
  },
  filterStatus: {
    type: String,
    default: "PUBLIC",
  },
});

const {t, locale} = useI18n();
const axios = injectAxios();
const groupsTableRef = ref(null);

const getGroups = async (params) => {
  params["search"] = props.filterText;
  params["status"] = props.filterStatus;
  params["currentLocale"] = locale.value;
  params["requestTrainers"] = true;

  return await axios.get('/training/groups/search', {params: params});
};

const resetGroupsTable = () => {
  groupsTableRef.value.reset();
};

const numberOfResults = () => {
  return groupsTableRef.value ? groupsTableRef.value.numberOfResults() : 0;
};

const deleteGroup = (group) => {
  EventBus.$emit('show-remove-group-modal', group)
};


watch([locale], resetGroupsTable);

const fields = computed(() => ([
  {key: 'translation', label: t("MODULE"), sortable: true},
  {key: 'title', label: t("TITLE"), sortable: true},
  {key: 'startEvent', label: t("START"), sortable: true},
  {key: 'endEvent', label: t("END"), sortable: true},
  {key: 'businessName', label: t("SCHOOL"), sortable: true},
  {key: "language", label: t("LANGUAGE"), sortable: true},
  {key: 'trainers', label: t("TRAINER"), sortable: false},
  {key: 'location', label: t("LOCATION"), sortable: true},
  {key: 'status', label: t("STATUS"), sortable: false},
  {key: 'groupSize', label: t("OCCUPANCY"), sortable: true},
  {key: 'action', label: "", sortable: false},
]));

const isAdmin = () => {
  return auth.hasRole("ADMIN");
};

defineExpose({
  numberOfResults,
  resetGroupsTable,
});
</script>
